import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions/permissionEngine';
import type { EstimateItem } from '../../shared/types/estimate.types';
import { estimateItemsService } from '../../shared/services/estimateItems.service';
import type {
  ApplyProcessTemplateInput,
  ProcessApplyPreview,
  ProcessApplyPreviewResource,
  ProcessApplyPreviewStep,
  WorkProcessStep,
  WorkProcessStepResource,
  WorkProcessTemplate,
} from '../types/processTemplate.types';
import { processTemplatesService } from './processTemplates.service';

function calculateStepQuantity(baseQuantity: number, step: WorkProcessStep) {
  return Number(step.default_quantity || 0) * Number(baseQuantity || 0);
}

function getPriceDate(value?: string | null) {
  return value ? new Date(value).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
}

function calculateResource(stepQuantity: number, resource: WorkProcessStepResource): ProcessApplyPreviewResource {
  const quantity = Number(resource.norm_quantity || 0) * stepQuantity * (1 + Number(resource.waste_factor || 0));
  const unitPrice = Number(resource.unit_price || 0);
  return {
    step_id: resource.step_id,
    resource_id: resource.resource_id || null,
    resource_code: resource.resource_code || null,
    resource_name: resource.resource_name,
    resource_type: resource.resource_type,
    unit: resource.unit,
    quantity,
    unit_price: unitPrice,
    total_amount: quantity * unitPrice,
    price_source: resource.price_source || 'work_process_template',
    price_date: resource.price_date || null,
    price_warning: unitPrice > 0 ? null : 'Chưa có đơn giá',
  };
}

async function resolveCurrentPrice(resource: WorkProcessStepResource, companyId: string) {
  if (!resource.resource_id) {
    return {
      unit_price: Number(resource.unit_price || 0),
      price_source: resource.price_source || 'work_process_template',
      price_date: resource.price_date || null,
      price_warning: Number(resource.unit_price || 0) > 0 ? null : 'Chưa liên kết tài nguyên để dò giá',
    };
  }

  if (resource.resource_type === 'labor') {
    const { data: laborRate, error: laborError } = await supabase
      .from('user_labor_rates' as any)
      .select('amount, rate_type, effective_from')
      .eq('company_id', companyId)
      .eq('user_id', resource.resource_id)
      .lte('effective_from', new Date().toISOString().slice(0, 10))
      .or(`effective_to.is.null,effective_to.gte.${new Date().toISOString().slice(0, 10)}`)
      .order('effective_from', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (laborError && laborError.code !== '42P01') throw laborError;
    if (laborRate) {
      return {
        unit_price: Number((laborRate as any).amount || 0),
        price_source: `user_labor_rates:${(laborRate as any).rate_type || 'labor'}`,
        price_date: getPriceDate((laborRate as any).effective_from),
        price_warning: Number((laborRate as any).amount || 0) > 0 ? null : 'Đơn giá nhân công bằng 0',
      };
    }
  }

  const { data: rate, error } = await supabase
    .from('rates' as any)
    .select('price, effective_from, status, supplier:suppliers(name)')
    .eq('resource_id', resource.resource_id)
    .in('status', ['CURRENT', 'current', 'ACTIVE', 'active'])
    .lte('effective_from', new Date().toISOString())
    .order('effective_from', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error && error.code !== '42P01') throw error;
  if (!rate) {
    return {
      unit_price: Number(resource.unit_price || 0),
      price_source: resource.price_source || 'work_process_template',
      price_date: resource.price_date || null,
      price_warning: Number(resource.unit_price || 0) > 0 ? null : 'Chưa có đơn giá hiện hành',
    };
  }

  const supplier = Array.isArray((rate as any).supplier) ? (rate as any).supplier[0] : (rate as any).supplier;
  return {
    unit_price: Number((rate as any).price || 0),
    price_source: supplier?.name ? `rates:${supplier.name}` : 'rates',
    price_date: getPriceDate((rate as any).effective_from),
    price_warning: Number((rate as any).price || 0) > 0 ? null : 'Đơn giá hiện hành bằng 0',
  };
}

async function calculateResourceWithCurrentPrice(stepQuantity: number, resource: WorkProcessStepResource, companyId: string) {
  const pricing = await resolveCurrentPrice(resource, companyId);
  const quantity = Number(resource.norm_quantity || 0) * stepQuantity * (1 + Number(resource.waste_factor || 0));

  return {
    step_id: resource.step_id,
    resource_id: resource.resource_id || null,
    resource_code: resource.resource_code || null,
    resource_name: resource.resource_name,
    resource_type: resource.resource_type,
    unit: resource.unit,
    quantity,
    unit_price: pricing.unit_price,
    total_amount: quantity * pricing.unit_price,
    price_source: pricing.price_source,
    price_date: pricing.price_date,
    price_warning: pricing.price_warning,
  };
}

async function calculateStepWithCurrentPrices(baseQuantity: number, step: WorkProcessStep, companyId: string): Promise<ProcessApplyPreviewStep> {
  const quantity = calculateStepQuantity(baseQuantity, step);
  const resources = await Promise.all((step.resources || []).map((resource) => calculateResourceWithCurrentPrice(quantity, resource, companyId)));

  return {
    step,
    quantity,
    resources,
    total_amount: resources.reduce((sum, resource) => sum + resource.total_amount, 0),
  };
}

function calculateStep(baseQuantity: number, step: WorkProcessStep): ProcessApplyPreviewStep {
  const quantity = calculateStepQuantity(baseQuantity, step);
  const resources = (step.resources || []).map((resource) => calculateResource(quantity, resource));
  return {
    step,
    quantity,
    resources,
    total_amount: resources.reduce((sum, resource) => sum + resource.total_amount, 0),
  };
}

export const processTemplateApplyService = {
  async preview(templateId: string, baseQuantity: number): Promise<ProcessApplyPreview | null> {
    const template = await processTemplatesService.getById(templateId);
    if (!template) return null;
    const steps = await Promise.all((template.steps || []).map((step) => calculateStepWithCurrentPrices(baseQuantity, step, template.company_id)));
    return {
      template,
      base_quantity: baseQuantity,
      steps,
      total_amount: steps.reduce((sum, step) => sum + step.total_amount, 0),
      warning_count: steps.reduce((sum, step) => sum + step.resources.filter((resource) => resource.price_warning).length, 0),
    };
  },

  buildPreview(template: WorkProcessTemplate, baseQuantity: number): ProcessApplyPreview {
    const steps = (template.steps || []).map((step) => calculateStep(baseQuantity, step));
    return {
      template,
      base_quantity: baseQuantity,
      steps,
      total_amount: steps.reduce((sum, step) => sum + step.total_amount, 0),
      warning_count: steps.reduce((sum, step) => sum + step.resources.filter((resource) => resource.price_warning).length, 0),
    };
  },

  buildPreviewFromEstimateItem(template: WorkProcessTemplate, item: EstimateItem): ProcessApplyPreview {
    return this.buildPreview(template, Number(item.quantity || 0));
  },

  async applyToEstimateItem(input: ApplyProcessTemplateInput): Promise<void> {
    const { data: itemRow, error: itemError } = await supabase
      .from('estimate_items' as any)
      .select('*')
      .eq('id', input.estimate_item_id)
      .single();

    if (itemError) throw itemError;
    const item = itemRow as EstimateItem;

    const { data: estimateRow, error: estimateError } = await supabase
      .from('estimate_headers' as any)
      .select('id, status_code, company_id')
      .eq('id', item.estimate_id)
      .single();

    if (estimateError) throw estimateError;
    if (['locked', 'converted_to_boq', 'cancelled'].includes(String((estimateRow as any).status_code))) {
      throw new Error('Dự toán đã khóa hoặc đã chuyển BOQ, không thể áp quy trình.');
    }

    await permissionEngine.assert(input.user_id, 'estimates.apply_process', { companyId: item.company_id });

    const template = await processTemplatesService.getById(input.template_id);
    if (!template) throw new Error('Không tìm thấy quy trình mẫu.');

    const selectedIds = new Set(input.selected_step_ids || []);
    const steps = (template.steps || []).filter((step) => selectedIds.size === 0 || selectedIds.has(step.id));
    const previewSteps = await Promise.all(steps.map((step) => calculateStepWithCurrentPrices(Number(item.quantity || 0), step, item.company_id)));
    const preview: ProcessApplyPreview = {
      template: { ...template, steps },
      base_quantity: Number(item.quantity || 0),
      steps: previewSteps,
      total_amount: previewSteps.reduce((sum, step) => sum + step.total_amount, 0),
      warning_count: previewSteps.reduce((sum, step) => sum + step.resources.filter((resource) => resource.price_warning).length, 0),
    };

    for (const previewStep of preview.steps) {
      const step = previewStep.step;
      const materialCost = previewStep.resources.filter((r) => r.resource_type === 'material' || r.resource_type === 'agro').reduce((sum, r) => sum + r.total_amount, 0);
      const laborCost = previewStep.resources.filter((r) => r.resource_type === 'labor').reduce((sum, r) => sum + r.total_amount, 0);
      const equipmentCost = previewStep.resources.filter((r) => r.resource_type === 'equipment').reduce((sum, r) => sum + r.total_amount, 0);
      const subcontractCost = previewStep.resources.filter((r) => ['service', 'subcontract'].includes(String(r.resource_type))).reduce((sum, r) => sum + r.total_amount, 0);

      let estimateItemId = item.id;
      if (step.estimate_item_mode !== 'resource_only') {
        const { data: createdItem, error: createItemError } = await supabase
          .from('estimate_items' as any)
          .insert({
            company_id: item.company_id,
            project_id: item.project_id,
            estimate_id: item.estimate_id,
            parent_id: step.estimate_item_mode === 'child' ? item.id : null,
            zone_id: item.zone_id,
            phase_id: item.phase_id,
            category_code: step.category_code,
            item_code: step.step_code,
            item_name: step.step_name,
            unit: step.unit,
            quantity: previewStep.quantity,
            unit_price: previewStep.quantity > 0 ? previewStep.total_amount / previewStep.quantity : 0,
            material_cost: materialCost,
            labor_cost: laborCost,
            equipment_cost: equipmentCost,
            subcontract_cost: subcontractCost,
            total_amount: previewStep.total_amount,
            status_code: 'draft',
            process_template_id: template.id,
            process_step_id: step.id,
            source_type: 'work_process_step',
            source_id: step.id,
            sort_order: Number(item.sort_order || 0) + Number(step.sort_order || 0) + 1,
          })
          .select('id')
          .single();

        if (createItemError) throw createItemError;
        estimateItemId = String((createdItem as any).id);
      }

      if (previewStep.resources.length > 0) {
        const { error: resourceError } = await supabase.from('estimate_item_resources' as any).insert(
          previewStep.resources.map((resource) => ({
            company_id: item.company_id,
            project_id: item.project_id,
            estimate_id: item.estimate_id,
            estimate_item_id: estimateItemId,
            resource_type: resource.resource_type,
            resource_id: resource.resource_id,
            resource_code: resource.resource_code,
            resource_name: resource.resource_name,
            unit: resource.unit,
            norm_quantity: previewStep.quantity > 0 ? resource.quantity / previewStep.quantity : 0,
            quantity: resource.quantity,
            waste_factor: 0,
            unit_price: resource.unit_price,
            total_amount: resource.total_amount,
            price_source: resource.price_source || 'work_process_template',
            price_date: resource.price_date,
            note: resource.price_warning,
            process_template_id: template.id,
            process_step_id: step.id,
            source_type: 'work_process_step_resource',
            source_id: resource.resource_id || step.id,
          })),
        );
        if (resourceError) throw resourceError;
      }
    }

    await supabase.from('estimate_change_logs' as any).insert({
      company_id: item.company_id,
      estimate_id: item.estimate_id,
      estimate_item_id: item.id,
      action_code: 'apply_process_template',
      new_value: { template_id: template.id, template_code: template.template_code, step_count: preview.steps.length },
      changed_by: input.user_id,
      note: `Áp quy trình mẫu ${template.template_code} - ${template.template_name}`,
    });

    await estimateItemsService.recalculateHeaderTotals(item.estimate_id);
  },
};
